import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { packageToursAPI, settingsAPI } from '../api';
import PackageTourTicket from './PackageTourTicket';

const fmt = (n) => '₺' + parseFloat(n||0).toLocaleString('tr-TR',{minimumFractionDigits:2});
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

const emptyTour = { name:'', start_date:'', end_date:'', description:'', guest_name_tour:'', pax_count:1, phone:'', tour_hotel:'', tour_room:'', sale_amount:'', sale_currency:'TRY', deposit_amount:'', deposit_currency:'TRY', cost_amount:'', cost_currency:'TRY', payment_channel:'Nakit', notes:'' };

export default function PackageTours() {
  const { currentFirm } = useAuth();
  const [tours, setTours] = useState([]);
  const [selected, setSelected] = useState(null);
  const [days, setDays] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyTour);
  const [editId, setEditId] = useState(null);
  const [dayForm, setDayForm] = useState({ day_number:'', day_date:'', program:'' });
  const [ticketData, setTicketData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(''),3000); };

  const load = useCallback(async () => {
    if (!currentFirm) return;
    const [toursRes, curRes] = await Promise.all([packageToursAPI.getAll(currentFirm.id), settingsAPI.getCurrencies(currentFirm.id)]);
    // API {tours, total} nesnesi döndürüyor; dizi dönen sürümlerle de uyumlu kalsın.
    setTours(Array.isArray(toursRes.data) ? toursRes.data : (toursRes.data?.tours || []));
    setCurrencies(Array.isArray(curRes.data) ? curRes.data : []);
  }, [currentFirm]);

  useEffect(() => { load(); }, [load]);

  const selectTour = async (t) => {
    setSelected(t);
    const dRes = await packageToursAPI.getDays(currentFirm.id, t.id);
    setDays(Array.isArray(dRes.data) ? dRes.data : (dRes.data?.days || []));
    setShowForm(false);
  };

  const saveTour = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editId) { await packageToursAPI.update(currentFirm.id, editId, form); setEditId(null); }
      else await packageToursAPI.create(currentFirm.id, form);
      setShowForm(false); load(); flash('Tur kaydedildi.');
    } catch(err) { flash(err.response?.data?.error||'Hata'); }
    finally { setSaving(false); }
  };

  const deleteTour = async (id) => {
    if (!window.confirm('Turu silmek istiyor musunuz?')) return;
    await packageToursAPI.delete(currentFirm.id, id);
    setSelected(null); load(); flash('Tur silindi.');
  };

  const addDay = async (e) => {
    e.preventDefault();
    await packageToursAPI.addDay(currentFirm.id, selected.id, dayForm);
    setDayForm({ day_number:'', day_date:'', program:'' });
    const dRes = await packageToursAPI.getDays(currentFirm.id, selected.id);
    setDays(dRes.data);
    flash('Gün eklendi.');
  };

  const deleteDay = async (dayId) => {
    await packageToursAPI.deleteDay(currentFirm.id, selected.id, dayId);
    const dRes = await packageToursAPI.getDays(currentFirm.id, selected.id);
    setDays(dRes.data);
    flash('Gün silindi.');
  };

  const openTicket = () => { if (!selected) return; setTicketData({ tour: selected, days, firm: { id:currentFirm.id, name:currentFirm.name } }); };

  const inp = { height:34, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, background:'var(--bg2)', color:'var(--text)', width:'100%' };
  const lbl = { fontSize:11, color:'var(--text2)', marginBottom:4, fontWeight:500 };

  if (!currentFirm) return <div style={{padding:'2rem',color:'var(--text3)',textAlign:'center'}}>Firma seçin.</div>;

  return (
    <div style={{display:'flex',gap:16,minHeight:'70vh'}}>
      {/* Sol: Tur listesi */}
      <div style={{width:260,flexShrink:0,display:'flex',flexDirection:'column',gap:10}}>
        <button onClick={()=>{setForm(emptyTour);setEditId(null);setShowForm(true);setSelected(null);}} className="btn btn-dark" style={{width:'100%'}}>+ Paket Tur Ekle</button>
        {msg&&<div style={{padding:'6px 10px',borderRadius:'var(--radius)',background:'var(--green-bg)',color:'var(--green)',fontSize:12}}>✓ {msg}</div>}
        {tours.map(t=>(
          <div key={t.id} onClick={()=>selectTour(t)}
            style={{background:selected?.id===t.id?'var(--blue-bg)':'var(--bg2)',border:`1px solid ${selected?.id===t.id?'var(--blue-border)':'var(--border)'}`,borderRadius:'var(--radius-lg)',padding:'10px 12px',cursor:'pointer'}}>
            <div style={{fontSize:13,fontWeight:500,color:selected?.id===t.id?'var(--blue-light)':'var(--text)',marginBottom:2}}>{t.name}</div>
            <div style={{fontSize:11,color:'var(--text3)'}}>{fmtDate(t.start_date)} → {fmtDate(t.end_date)}</div>
            <div style={{fontSize:11,color:'var(--green)',marginTop:2}}>{fmt(t.sale_amount_try||t.sale_amount)}</div>
          </div>
        ))}
        {tours.length===0&&<div style={{textAlign:'center',padding:'2rem',color:'var(--text3)',fontSize:12}}>Paket tur yok.</div>}
      </div>

      {/* Sağ: Detay */}
      <div style={{flex:1,display:'flex',flexDirection:'column',gap:12}}>
        {showForm && (
          <div className="card">
            <div style={{fontSize:14,fontWeight:600,marginBottom:14}}>{editId?'Tur Düzenle':'Yeni Paket Tur'}</div>
            <form onSubmit={saveTour}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
                <div style={{gridColumn:'1/-1'}}><div style={lbl}>Tur Adı *</div><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={inp} required/></div>
                <div><div style={lbl}>Başlangıç</div><input type="date" value={form.start_date} onChange={e=>setForm(f=>({...f,start_date:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Bitiş</div><input type="date" value={form.end_date} onChange={e=>setForm(f=>({...f,end_date:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Ödeme Kanalı</div><select value={form.payment_channel} onChange={e=>setForm(f=>({...f,payment_channel:e.target.value}))} style={inp}>{['Nakit','Banka Havalesi','Kredi Kartı','EFT'].map(v=><option key={v} value={v}>{v}</option>)}</select></div>
                <div><div style={lbl}>Misafir Adı</div><input value={form.guest_name_tour} onChange={e=>setForm(f=>({...f,guest_name_tour:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Kişi Sayısı</div><input type="number" value={form.pax_count} onChange={e=>setForm(f=>({...f,pax_count:e.target.value}))} style={inp} min="1"/></div>
                <div><div style={lbl}>Telefon</div><input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Otel / Konaklama</div><input value={form.tour_hotel} onChange={e=>setForm(f=>({...f,tour_hotel:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Oda No</div><input value={form.tour_room} onChange={e=>setForm(f=>({...f,tour_room:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Satış Tutarı *</div><input type="number" value={form.sale_amount} onChange={e=>setForm(f=>({...f,sale_amount:e.target.value}))} style={inp} required min="0" step="0.01"/></div>
                <div><div style={lbl}>Satış Dövizi</div><select value={form.sale_currency} onChange={e=>setForm(f=>({...f,sale_currency:e.target.value}))} style={inp}>{currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
                <div><div style={lbl}>Kapora</div><input type="number" value={form.deposit_amount} onChange={e=>setForm(f=>({...f,deposit_amount:e.target.value}))} style={inp} min="0" step="0.01"/></div>
                <div><div style={lbl}>Maliyet</div><input type="number" value={form.cost_amount} onChange={e=>setForm(f=>({...f,cost_amount:e.target.value}))} style={inp} min="0" step="0.01"/></div>
                <div><div style={lbl}>Maliyet Dövizi</div><select value={form.cost_currency} onChange={e=>setForm(f=>({...f,cost_currency:e.target.value}))} style={inp}>{currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
                <div style={{gridColumn:'1/-1'}}><div style={lbl}>Not</div><textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inp,height:50,resize:'vertical',paddingTop:8}}/></div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button type="submit" disabled={saving} className="btn btn-dark">{saving?'Kaydediliyor...':'Kaydet'}</button>
                <button type="button" onClick={()=>setShowForm(false)} className="btn btn-ghost">İptal</button>
              </div>
            </form>
          </div>
        )}

        {selected && !showForm && (
          <>
            <div className="card">
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:16,fontWeight:600}}>{selected.name}</div>
                  <div style={{fontSize:12,color:'var(--text3)',marginTop:3}}>{fmtDate(selected.start_date)} → {fmtDate(selected.end_date)} · {selected.pax_count||1} kişi</div>
                  {selected.guest_name_tour&&<div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>Misafir: {selected.guest_name_tour}</div>}
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button onClick={openTicket} className="btn btn-ghost btn-sm">Bilet</button>
                  <button onClick={()=>{const f=selected;setForm({name:f.name||'',start_date:f.start_date?.slice(0,10)||'',end_date:f.end_date?.slice(0,10)||'',description:f.description||'',guest_name_tour:f.guest_name_tour||'',pax_count:f.pax_count||1,phone:f.phone||'',tour_hotel:f.tour_hotel||'',tour_room:f.tour_room||'',sale_amount:f.sale_amount||'',sale_currency:f.sale_currency||'TRY',deposit_amount:f.deposit_amount||'',deposit_currency:f.deposit_currency||'TRY',cost_amount:f.cost_amount||'',cost_currency:f.cost_currency||'TRY',payment_channel:f.payment_channel||'Nakit',notes:f.notes||''});setEditId(f.id);setShowForm(true);}} className="btn btn-ghost btn-sm">Düzenle</button>
                  <button onClick={()=>deleteTour(selected.id)} className="btn btn-sm" style={{background:'var(--red-bg)',color:'var(--red)',border:'1px solid var(--red-border)'}}>Sil</button>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginTop:12}}>
                {[
                  ['Satış', fmt(selected.sale_amount_try||selected.sale_amount)],
                  ['Kapora', fmt(selected.deposit_amount||0)],
                  ['Maliyet', fmt(selected.cost_amount_try||selected.cost_amount||0)],
                  ['Kâr', fmt((selected.sale_amount_try||0)-(selected.cost_amount_try||0))],
                ].map(([l,v])=>(
                  <div key={l} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'8px 10px'}}>
                    <div style={{fontSize:10,color:'var(--text3)',marginBottom:2}}>{l}</div>
                    <div style={{fontSize:13,fontWeight:600}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Günler */}
            <div className="card">
              <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Gezi Programı</div>
              <form onSubmit={addDay} style={{display:'flex',gap:8,marginBottom:12,alignItems:'flex-end'}}>
                <div><div style={lbl}>Gün No</div><input type="number" value={dayForm.day_number} onChange={e=>setDayForm(f=>({...f,day_number:e.target.value}))} style={{...inp,width:70}} required min="1"/></div>
                <div><div style={lbl}>Tarih</div><input type="date" value={dayForm.day_date} onChange={e=>setDayForm(f=>({...f,day_date:e.target.value}))} style={{...inp,width:150}}/></div>
                <div style={{flex:1}}><div style={lbl}>Program *</div><input value={dayForm.program} onChange={e=>setDayForm(f=>({...f,program:e.target.value}))} style={inp} placeholder="Günün programı..." required/></div>
                <button type="submit" className="btn btn-dark btn-sm">+ Ekle</button>
              </form>
              {days.sort((a,b)=>a.day_number-b.day_number).map(d=>(
                <div key={d.id} style={{display:'flex',gap:14,padding:'10px 0',borderBottom:'1px solid var(--border2)',alignItems:'flex-start'}}>
                  <div style={{minWidth:60,fontWeight:700,color:'var(--blue-light)',fontSize:13,flexShrink:0}}>Gün {d.day_number}</div>
                  {d.day_date&&<div style={{minWidth:100,color:'var(--text3)',fontSize:12,flexShrink:0}}>{fmtDate(d.day_date)}</div>}
                  <div style={{flex:1,fontSize:13,color:'var(--text)',lineHeight:1.6}}>{d.program}</div>
                  <button onClick={()=>deleteDay(d.id)} style={{width:22,height:22,border:'1px solid var(--red-border)',borderRadius:3,background:'var(--red-bg)',color:'var(--red)',cursor:'pointer',fontSize:12,flexShrink:0}}>×</button>
                </div>
              ))}
              {days.length===0&&<div style={{textAlign:'center',padding:'1rem',color:'var(--text3)',fontSize:12}}>Henüz gün eklenmedi.</div>}
            </div>
          </>
        )}

        {!selected && !showForm && (
          <div className="card" style={{textAlign:'center',padding:'3rem',color:'var(--text3)'}}>
            
            <div style={{fontSize:13}}>Listeden bir tur seçin veya yeni tur ekleyin.</div>
          </div>
        )}
      </div>

      {ticketData && <PackageTourTicket data={ticketData} onClose={()=>setTicketData(null)}/>}
    </div>
  );
}
